// IndexedDB service for sound caching and offline support

interface CachedSound {
  id: string
  name: string
  label: string
  category: string
  filePath: string
  createdAt: string
  audioData: ArrayBuffer
  cachedAt: number
  size: number
}

interface CacheStats {
  totalSounds: number
  totalSize: number
  lastUpdated: number
}

class CacheService {
  private dbName = 'SoundBoardCache'
  private dbVersion = 1
  private storeName = 'sounds'
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('IndexedDB initialized successfully')
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create sounds store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' })
          store.createIndex('filePath', 'filePath', { unique: true })
          store.createIndex('category', 'category', { unique: false })
          store.createIndex('cachedAt', 'cachedAt', { unique: false })
        }
      }
    })
  }

  async cacheSound(sound: any, audioData: ArrayBuffer): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    const cachedSound: CachedSound = {
      ...sound,
      audioData,
      cachedAt: Date.now(),
      size: audioData.byteLength
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.put(cachedSound)

      request.onsuccess = () => {
        console.log(`Cached sound: ${sound.label}`)
        resolve()
      }

      request.onerror = () => {
        console.error(`Failed to cache sound: ${sound.label}`, request.error)
        reject(request.error)
      }
    })
  }

  async getCachedSound(soundId: string): Promise<CachedSound | null> {
    if (!this.db) {
      return null
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.get(soundId)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        console.error(`Failed to get cached sound: ${soundId}`, request.error)
        reject(request.error)
      }
    })
  }

  async isSoundCached(soundId: string): Promise<boolean> {
    const cached = await this.getCachedSound(soundId)
    return cached !== null
  }

  async getCachedSoundBlob(soundId: string): Promise<Blob | null> {
    const cached = await this.getCachedSound(soundId)
    if (!cached) {
      return null
    }

    return new Blob([cached.audioData], { type: 'audio/mpeg' })
  }

  async getAllCachedSounds(): Promise<CachedSound[]> {
    if (!this.db) {
      return []
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.getAll()

      request.onsuccess = () => {
        resolve(request.result || [])
      }

      request.onerror = () => {
        console.error('Failed to get all cached sounds', request.error)
        reject(request.error)
      }
    })
  }

  async getCacheStats(): Promise<CacheStats> {
    const cachedSounds = await this.getAllCachedSounds()

    return {
      totalSounds: cachedSounds.length,
      totalSize: cachedSounds.reduce((total, sound) => total + sound.size, 0),
      lastUpdated: cachedSounds.length > 0 ? Math.max(...cachedSounds.map(s => s.cachedAt)) : 0
    }
  }

  async clearCache(): Promise<void> {
    if (!this.db) {
      return
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.clear()

      request.onsuccess = () => {
        console.log('Cache cleared successfully')
        resolve()
      }

      request.onerror = () => {
        console.error('Failed to clear cache', request.error)
        reject(request.error)
      }
    })
  }

  async removeCachedSound(soundId: string): Promise<void> {
    if (!this.db) {
      return
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.delete(soundId)

      request.onsuccess = () => {
        console.log(`Removed cached sound: ${soundId}`)
        resolve()
      }

      request.onerror = () => {
        console.error(`Failed to remove cached sound: ${soundId}`, request.error)
        reject(request.error)
      }
    })
  }

  // Format cache size for display
  formatCacheSize(bytes: number): string {
    if (bytes === 0) return '0 B'

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }
}

// Export singleton instance
export const cacheService = new CacheService()
